import Box from '@mui/material/Box';
import CloudOffRoundedIcon from '@mui/icons-material/CloudOffRounded';
import WifiOffRoundedIcon from '@mui/icons-material/WifiOffRounded';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast, ToastOptions } from 'react-toastify';
import {
  WS_CONNECTION_STATUS_EVENT,
  WsConnectionStatus,
  WsConnector,
} from '_apollo/WsSubscriptionClient';

const TOAST_ID = 'connection-status';

// Brief WS blips (token refresh, a single dropped frame) recover within 1–2
// attempts. Only surface the indicator once reconnection is clearly struggling,
// so routine churn does not flash a toast at the user.
const WS_UNHEALTHY_THRESHOLD = 3;

// Strip react-toastify's default chrome (coloured variant background, icon,
// progress bar, heavy shadow) so only our minimal themed pill shows. The toast
// content is rendered inside the app shell's <ToastContainer/>, which sits under
// ThemeCustomizationWrapper — so the sx theme tokens below resolve to the active
// course/company theme.
const MINIMAL_TOAST_OPTIONS: ToastOptions = {
  icon: false,
  closeButton: false,
  hideProgressBar: true,
  closeOnClick: false,
  draggable: false,
  style: { background: 'transparent', boxShadow: 'none', padding: 0 },
};

const ConnectionToastContent: React.FC<{
  message: string;
  offline: boolean;
}> = ({ message, offline }) => {
  const Icon = offline ? WifiOffRoundedIcon : CloudOffRoundedIcon;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        bgcolor: 'background.paper',
        color: 'text.secondary',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1.5,
        px: 1.5,
        py: 0.75,
        fontSize: 13,
        lineHeight: 1.4,
      }}
    >
      <Icon sx={{ fontSize: 18, flexShrink: 0, color: 'text.disabled' }} />
      {message}
    </Box>
  );
};

/**
 * Global, headless connectivity indicator. Renders nothing itself; it drives a
 * single persistent toast (via the app shell's <ToastContainer/>) shown while the
 * client is offline or the WebSocket cannot reach the backend, and auto-dismisses
 * it — with a brief "connection restored" confirmation — once connectivity returns.
 *
 * Two complementary signals (see the design discussion): `navigator.onLine` for
 * instant feedback on a hard offline (Wi-Fi/data off), and the WS reconnect state
 * for the "online but server unreachable" case that `navigator.onLine` misses.
 */
export const ConnectionStatusNotifier: React.FC = () => {
  const { t } = useTranslation();
  const [online, setOnline] = React.useState(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const [wsAttempts, setWsAttempts] = React.useState(0);
  const [wsLost, setWsLost] = React.useState(false);
  const wasDisconnectedRef = React.useRef(false);

  // navigator online/offline — hard connectivity loss.
  React.useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // WS reconnect state — server reachability while the device reports "online".
  React.useEffect(() => {
    const handler = (status: WsConnectionStatus) => {
      if (status.state === 'connected') {
        setWsAttempts(0);
        setWsLost(false);
      } else if (status.state === 'reconnecting') {
        setWsAttempts(status.attempt);
      } else if (status.state === 'lost') {
        setWsLost(true);
      }
    };
    WsConnector.on(WS_CONNECTION_STATUS_EVENT, handler);
    return () => {
      WsConnector.off(WS_CONNECTION_STATUS_EVENT, handler);
    };
  }, []);

  const wsUnhealthy = wsLost || wsAttempts >= WS_UNHEALTHY_THRESHOLD;
  const disconnected = !online || wsUnhealthy;

  React.useEffect(() => {
    if (disconnected) {
      wasDisconnectedRef.current = true;
      const message = !online
        ? t('generic.offline')
        : t('generic.serverUnreachable');
      const content = (
        <ConnectionToastContent message={message} offline={!online} />
      );
      if (toast.isActive(TOAST_ID)) {
        toast.update(TOAST_ID, { render: () => content });
      } else {
        toast(content, {
          ...MINIMAL_TOAST_OPTIONS,
          toastId: TOAST_ID,
          autoClose: false,
        });
      }
    } else if (wasDisconnectedRef.current) {
      // Connectivity returned: just remove the indicator, no confirmation toast.
      wasDisconnectedRef.current = false;
      toast.dismiss(TOAST_ID);
    }
  }, [disconnected, online, t]);

  return null;
};
